import type { FoundationFieldName } from '@@/api/learnings/foundation/foundation';
import type { OutcomeReading, ProgramOutcome } from '@@/api/program';
import type { LibraryRow } from '@/state/experiments/library-learnings';
import { Link } from 'react-router';
import { debriefKind, foundationOf } from '@@/api/learnings/foundation/foundation';
import { hasPxlScores } from '@@/api/program/program-rows';
import { Button } from '@/components/ui/button';
import { NavLink } from '@/components/NavLink';
import { OUTCOME_LABELS, mechanismLabel } from '@/state/experiments/library-learnings';
import { parseMechanisms } from '@/state/experiments/expected-mechanisms';
import { cn } from 'helpers/cn';
import { MICRO_LABEL_SHAPE } from '../../ConsolePanel';
import { Section } from '../Learnings/parts';
import { TeamRead, WhatChanged, WhatItTaught } from './LearningsInsight';

/**
 * Colour is the verdict: a win reads ok, a loss danger, a split warn. Inconclusive is
 * not a failure, so it gets the neutral hairline rather than a colour implying one.
 */
const OUTCOME_TONE: Record<ProgramOutcome, string> = {
  won: 'border-ok-line text-ok',
  lost: 'border-danger-line text-danger',
  mixed: 'border-warn-line text-warn',
  inconclusive: 'border-line text-dim',
  'no-data': 'border-line text-dim',
};

const signed = (value: number, digits = 1) => `${value > 0 ? '+' : ''}${value.toFixed(digits)}%`;

type ReadingProps = { reading: OutcomeReading | null };

export const ResultChip = ({ reading }: ReadingProps) => {
  if (!reading) return <span className="text-dim">-</span>;

  return (
    <span
      className={cn(
        'micro-label rounded-pill inline-flex items-center gap-1.5 border px-2 py-1',
        OUTCOME_TONE[reading.outcome],
      )}
    >
      {OUTCOME_LABELS[reading.outcome]}
      {/* As Program labels it: the outcome word agrees, and so should the label. */}
      {reading.byDeployment && <span> · deployed</span>}
      {/* tabular-nums, not `num`: that utility also sets a larger font size, which knocked
          the figure off the label's baseline inside a nano-sized chip. */}
      {reading.lift !== null && <span className="tabular-nums">{signed(reading.lift)}</span>}
    </span>
  );
};

/**
 * What a past test tried and what it taught, without opening it, in the Learnings debrief's parts.
 *
 * On a website with learnings on, the AI card leads: what each variant changed, then what the test
 * taught, with the team's read beside it. The side holds the concluded result, what the test has
 * written down (its foundation, the homework that makes a learning sharp), and the way to the full
 * debrief. A website without learnings shows only the side: no card blocks, nothing claimed.
 */
/** The id the chevron points aria-controls at. */
export const learningsPanelId = (id: number) => `learnings-${id}`;

const FOUNDATION_LABELS: Record<FoundationFieldName, string> = {
  hypothesis: 'Hypothesis',
  description: 'Notes',
  mechanism: 'Expected mechanism',
  pxl: 'PXP score',
  debrief: 'Debrief',
};

type LearningsDetailProps = { row: LibraryRow };

export const LearningsDetail = ({ row }: LearningsDetailProps) => {
  const variantNames = new Map((row.variants ?? []).map((variant) => [variant.id, variant.name]));
  const hasCard = row.insight?.status === 'generated';

  return (
    <div
      id={learningsPanelId(row.id)}
      className="border-line bg-panel2 rounded-card grid gap-3 border p-3 lg:grid-cols-[minmax(0,2.2fr)_minmax(260px,1fr)]"
    >
      <div className="grid min-w-0 content-start gap-3">
        {row.insight && <WhatChanged insight={row.insight} variantNames={variantNames} />}
        {row.insight && <WhatItTaught insight={row.insight} />}
        {row.teamRead && <TeamRead teamRead={row.teamRead} />}
      </div>

      <div className="grid min-w-0 content-start gap-3">
        <Section title="Result">
          <div className="grid gap-2 p-3.5 text-meta">
            <ReadingSummary reading={row.reading} />
            <ul className="text-dim m-0 grid list-none gap-0.5 p-0">
              {(row.variants ?? []).map((variant) => (
                <li key={variant.id} className="truncate">
                  {variant.name}
                  {variant.deployed && <span className="text-accent"> · deployed</span>}
                </li>
              ))}
            </ul>
          </div>
        </Section>
        <Foundation row={row} />
        <div className="flex flex-wrap gap-2">
          {hasCard && (
            <Button asChild size="sm">
              <Link to={{ pathname: '/experiments/learnings', search: `?test=${row.id}` }}>Open debrief</Link>
            </Button>
          )}
          <Button asChild size="sm" variant="outline">
            <NavLink to={`/experiments/${row.id}`}>Open experiment</NavLink>
          </Button>
        </div>
      </div>
    </div>
  );
};

type FoundationProps = { row: LibraryRow };

/** What the test has written down: each field with a dot, filled when it is in place. */
const Foundation = ({ row }: FoundationProps) => {
  const mechanisms = parseMechanisms(row.expectedMechanisms);
  // Trimmed, as the filter trims it: whitespace alone drew an empty pill.
  const labels = [...mechanisms.selected.map(mechanismLabel), mechanisms.custom.trim()].filter(Boolean);
  const hypothesis = row.hypothesis.trim();
  const description = row.description.trim();
  const foundation = foundationOf({
    hypothesis,
    description,
    mechanisms: labels,
    scored: hasPxlScores(row.pxlScores),
    debrief: debriefKind(row.teamRead),
  });
  const values: Record<FoundationFieldName, string | null> = {
    hypothesis: hypothesis || null,
    description: description || null,
    mechanism: labels.join(', ') || null,
    pxl: foundation.pxl.status === 'ok' ? 'Scored' : null,
    debrief: foundation.debrief.status === 'ok' ? 'Written' : null,
  };
  const fields = Object.keys(FOUNDATION_LABELS) as FoundationFieldName[];
  const gaps = fields.filter((field) => foundation[field].status !== 'ok').length;

  return (
    <Section
      title="Foundation"
      actions={
        gaps > 0 ? (
          <span className={cn(MICRO_LABEL_SHAPE, 'text-warn')}>
            {gaps} {gaps === 1 ? 'gap' : 'gaps'}
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col px-3.5 pb-2 pt-1">
        {fields.map((field) => {
          const entry = foundation[field];
          const ok = entry.status === 'ok';
          return (
            <div key={field} className="border-line flex gap-2.5 border-b py-2 last:border-b-0">
              <span
                className={cn(
                  'mt-1 size-2 shrink-0 rounded-pill border-2',
                  ok ? 'border-ok bg-ok' : 'border-warn bg-transparent',
                )}
                aria-hidden="true"
              />
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-meta font-medium">{FOUNDATION_LABELS[field]}</span>
                {values[field] && <span className="text-dim whitespace-pre-line text-meta">{values[field]}</span>}
                {!ok && <span className="text-warn text-meta">{entry.note}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const ReadingSummary = ({ reading }: ReadingProps) => {
  if (!reading) return <p className="text-dim">Not concluded yet.</p>;

  const { variantName, lift, liftLower, liftUpper, byDeployment, statisticalOutcome } = reading;
  const interval =
    liftLower !== null && liftUpper !== null ? ` (${signed(liftLower * 100)} to ${signed(liftUpper * 100)})` : '';

  return (
    <div className="grid gap-1">
      <ResultChip reading={reading} />
      {variantName && lift !== null && (
        <p className="text-dim">
          {variantName}: {signed(lift)}
          {interval}
        </p>
      )}
      {/* Said out loud, because it changes what the win means: a person shipped it,
          the statistics alone did not call it. */}
      {byDeployment && statisticalOutcome !== 'won' && (
        <p className="text-dim">
          Counted as a win because a variant was deployed; the statistics read it as{' '}
          {OUTCOME_LABELS[statisticalOutcome].toLowerCase()}.
        </p>
      )}
    </div>
  );
};
